import { calculatePolygonCentroid } from 'components/BaseMap/util/BaseMap.utils';
import IpawsAuthoringSections from 'features/Weather/features/WeatherAlerts/features/AlertCreator/components/IpawsAlertFields/components/IpawsAuthoringSections/IpawsAuthoringSections';
import IpawsAffectedAreaSection from 'features/Weather/features/WeatherAlerts/features/AlertCreator/components/IpawsAlertFields/components/IpawsAffectedAreaSection/IpawsAffectedAreaSection';
import {
  formatIpawsDateTimeWithOffset,
  getIpawsDraftFindings,
} from 'features/Weather/features/WeatherAlerts/features/AlertCreator/utils/alertCreator.utils';
import {
  permittedAreaForDraft,
  resolvedFromAuthorized,
} from 'features/Weather/features/WeatherAlerts/features/AlertCreator/utils/femaJurisdictions.utils';
import { getFemaSubmissionContractOptions } from 'features/Weather/features/WeatherAlerts/features/AlertCreator/utils/femaSubmissionContract.utils';
import { readIpawsChannelRules } from 'features/Weather/features/WeatherAlerts/features/AlertCreator/utils/ipawsChannelRules.utils';
import { useEffect, useMemo, useState } from 'react';
import { Alert } from 'react-bootstrap';
import {
  useGetFemaAlertContractQuery,
  useGetFemaJurisdictionsQuery,
  useGetFemaMetaQuery,
} from 'services/ipaws/ipaws.api';
import { describeFemaDestination } from 'features/Weather/features/WeatherAlerts/features/AlertCreator/utils/femaMeta.utils';
import {
  acceptFemaJurisdictionsForClass,
  getChosenFemaEnvironment,
} from 'features/Weather/features/WeatherAlerts/features/AlertCreator/utils/femaEnvironment.utils';
import { FEMA_JURISDICTIONS_CLASS_MISMATCH } from 'features/Weather/features/WeatherAlerts/features/AlertCreator/components/IpawsAlertFields/config/femaEnvironment.config';
import FemaEnvironmentSelect from 'features/Weather/features/WeatherAlerts/features/AlertCreator/components/AlertModeSelector/components/FemaEnvironmentSelect/FemaEnvironmentSelect';
import {
  IPAWS_ACTION_STATUS_OPTIONS,
  getIpawsActionErrorMessage,
  getIpawsActionIssues,
  withIpawsActionStatusFinding,
  withoutServiceTimingFindings,
} from '../../../utils/ipawsActionPayload.utils';
import { normalizeIpawsActionDraft } from '../../../utils/ipawsActionDraft.utils';
import {
  getIpawsScopeClassFinding,
  getIpawsScopeOptionsForClass,
  getIpawsStatusClassFinding,
  getIpawsStatusOptionsForClass,
  resolveIpawsFemaClass,
} from '../../../utils/ipawsSendMode.utils';
import {
  scopeOptions,
  statusOptions,
} from 'features/Weather/features/WeatherAlerts/features/AlertCreator/components/IpawsAlertFields/config/Ipaws.config';
import IpawsDeviceMap from './IpawsDeviceMap';
import 'features/Weather/features/WeatherAlerts/features/AlertCreator/assets/alertCreator.scss';

export const validatePublishIpawsAlert = (data) => getIpawsActionIssues(data).length === 0;

/**
 * One sentence for each CAP status under the Status select (Mike, 2026-09-26). The CAP status is
 * the one control: no other field turns the send on or off.
 */
export const IPAWS_ACTION_STATUS_HELP = {
  Actual: 'Sends and disseminates when the Global Action triggers.',
  Test: 'Sends a Test message to FEMA. Never sent from the production environment.',
  Draft: 'Saved. Never sent.',
};

/** The finding for a stored draft action whose message status was not Draft. */
export const IPAWS_SAVED_AS_DRAFT_FINDING =
  'This action was saved as a draft; choose Actual to send.';

/**
 * The meta that the FEMA environment select and the destination banner read. The Global Action
 * editor shows no default class: the person selects the class, and the save refuses an action
 * without one. With `defaultEnvironment: null` the select shows an empty choice and the banner
 * says "Select a FEMA environment." until then.
 */
const withoutDefaultFemaEnvironment = (femaMetaQuery) =>
  femaMetaQuery.data && Array.isArray(femaMetaQuery.data.environments)
    ? { ...femaMetaQuery, data: { ...femaMetaQuery.data, defaultEnvironment: null } }
    : femaMetaQuery;

const PublishIpawsAlert = ({ setData, data, setError }) => {
  const draft = normalizeIpawsActionDraft(data);
  const [hasReviewedDraft, setHasReviewedDraft] = useState(false);
  // Multi-COG contract §1: the class lives in the action data as `femaEnvironment` and is saved
  // as `params.environment`. `null` = no class yet (a new action or a contractVersion 2 row).
  const femaEnvironment = draft.femaEnvironment ?? null;
  // One `/jurisdictions` answer per class. The query asks for the action's class, and an answer
  // that echoes another class is not used.
  const jurisdictionsQuery = useGetFemaJurisdictionsQuery(
    femaEnvironment ? { environment: femaEnvironment } : undefined,
  );
  const { data: jurisdictionsData, mismatch: isJurisdictionsClassMismatch } =
    acceptFemaJurisdictionsForClass(jurisdictionsQuery.data, femaEnvironment);
  const contractQuery = useGetFemaAlertContractQuery();
  const femaMetaQuery = useGetFemaMetaQuery();
  const femaSelectMetaQuery = withoutDefaultFemaEnvironment(femaMetaQuery);
  const femaDestination = describeFemaDestination(femaSelectMetaQuery, femaEnvironment);
  // THE ONE READER (`resolveFemaEnvironmentClass`): the action's class, else the class that an
  // older backend serves, else `null`. The status and scope rules by class read it.
  const femaClass = resolveIpawsFemaClass(femaEnvironment, femaMetaQuery.data);
  // The same reader in upper case, for the WEA Handling filter.
  const chosenFemaEnvironment = getChosenFemaEnvironment(femaMetaQuery.data, femaEnvironment);
  // An older backend (no `environments[]`) serves one class. The reader gives it with nothing
  // chosen, so the editor stores it: the person cannot choose another class. Production is
  // never stored by default (contract §0).
  const impliedFemaEnvironment = resolveIpawsFemaClass(null, femaMetaQuery.data);
  const jurisdictions = useMemo(() => jurisdictionsData?.jurisdictions ?? [], [jurisdictionsData]);
  const contractOptions = useMemo(
    () => getFemaSubmissionContractOptions(contractQuery.data),
    [contractQuery.data],
  );
  // PRODUCTION greys out Test, Draft and every scope but Public; TDL and DEMO offer them all.
  // The findings keep the full contract options, so a stored Test, Draft, Restricted or Private
  // action gets the class finding below.
  const authoringContractOptions = useMemo(
    () => ({
      ...contractOptions,
      status: getIpawsStatusOptionsForClass(contractOptions?.status ?? statusOptions, femaClass),
      scope: getIpawsScopeOptionsForClass(contractOptions?.scope ?? scopeOptions, femaClass),
    }),
    [contractOptions, femaClass],
  );
  // The same channel rules as the Alert Creator (card FE-06).
  const channelRules = useMemo(
    () => readIpawsChannelRules(jurisdictionsData, contractQuery.data),
    [contractQuery.data, jurisdictionsData],
  );
  const isJurisdictionsError = jurisdictionsQuery.isError || isJurisdictionsClassMismatch;
  const authorizedAreas = {
    jurisdictions,
    cogId: jurisdictionsData?.cogId,
    organizationName: jurisdictionsData?.organizationName ?? null,
    tenantNote: jurisdictionsData?.tenantNote,
    isLoading: jurisdictionsQuery.isLoading,
    isError: isJurisdictionsError,
    error: isJurisdictionsClassMismatch
      ? { message: FEMA_JURISDICTIONS_CLASS_MISMATCH }
      : jurisdictionsQuery.error,
  };
  const jurisdictionsState = {
    isLoading: jurisdictionsQuery.isLoading,
    isError: isJurisdictionsError,
  };
  const permittedArea = permittedAreaForDraft(draft, jurisdictions, jurisdictionsState);
  // The channel-aware findings for a draft. The Global Actions service sets the effective and
  // expiration times (card BE-25), so their findings do not apply here. The affected-area
  // findings stay: the map needs them. A Global Action can save a Draft message, so the status
  // check accepts Draft. The status class finding comes first.
  const statusClassFinding = getIpawsStatusClassFinding(draft.status, femaClass);
  const scopeClassFinding = getIpawsScopeClassFinding(draft.scope, femaClass);
  const draftFindings = [
    ...(statusClassFinding ? [statusClassFinding] : []),
    ...(scopeClassFinding ? [scopeClassFinding] : []),
    ...withIpawsActionStatusFinding(
      withoutServiceTimingFindings(
        getIpawsDraftFindings(draft, {
          permittedArea,
          controlledOptions: { ...contractOptions, status: IPAWS_ACTION_STATUS_OPTIONS },
          channelRules,
          authorizedJurisdictions: jurisdictions,
        }),
      ),
      draft.status,
    ),
  ];
  const actionIssues = getIpawsActionIssues(draft);
  // The status findings show at once, under the Status select: the class finding and the
  // missing-status issue. The other findings show after the person reviews the draft.
  const findings = hasReviewedDraft
    ? draftFindings
    : draftFindings.filter(({ fieldPath }) => fieldPath === 'status' || fieldPath === 'scope');
  // A stored draft action whose message status was not Draft opens as Draft
  // (`normalizeInitialData`). The finding stays until the person chooses another status.
  const savedAsDraft = draft.savedAsDraft === true && draft.status === 'Draft';
  // One sentence for each status that the select offers, and for the stored status.
  const statusHelpValues = [
    ...new Set([
      ...authoringContractOptions.status
        .filter(({ isDisabled }) => !isDisabled)
        .map(({ value }) => value),
      ...(draft.status ? [draft.status] : []),
    ]),
  ].filter((value) => IPAWS_ACTION_STATUS_HELP[value]);

  // Every write stores the draft and its save issues. The CAP status is the one send control:
  // the backend derives the stored approval from it.
  const update = (changes) => {
    const next = { ...draft, ...changes };
    const issues = getIpawsActionIssues(next);
    next.error = issues.length > 0;
    setData(next);
    setError(next.error ? { message: getIpawsActionErrorMessage(issues) } : null);
  };

  // A new action starts with no status and no class. The person must choose both to save.
  // An older backend serves one class; the mount write stores it (never production).
  const isNewAction = !data || Object.keys(data).length === 0;
  const storableImpliedClass =
    impliedFemaEnvironment !== null && impliedFemaEnvironment !== 'production'
      ? impliedFemaEnvironment
      : null;
  useEffect(() => {
    if (isNewAction) {
      setData({ ...draft, status: '', femaEnvironment: storableImpliedClass, error: true });
    }
  }, []);

  // A stored action with no class (a contractVersion 2 row), or a new action whose meta came in
  // after the mount: an older backend's one class is stored (never production).
  useEffect(() => {
    if (!isNewAction && femaEnvironment === null && storableImpliedClass !== null) {
      update({ femaEnvironment: storableImpliedClass });
    }
  }, [isNewAction, femaEnvironment, storableImpliedClass]);

  // A change from one class to another clears the resolved jurisdiction and the geocodes: each
  // class has its own COG profile, so an area of the old class can be outside the new COG. The
  // first class (from `null`) clears nothing. The same rule as the Alert Creator.
  const selectFemaEnvironment = (next) => {
    if (next === femaEnvironment) return;
    update(
      femaEnvironment === null
        ? { femaEnvironment: next }
        : { femaEnvironment: next, resolvedJurisdiction: null, geocodes: [] },
    );
  };

  const selectAuthorizedJurisdiction = (jurisdiction) => {
    const resolvedJurisdiction = resolvedFromAuthorized(jurisdiction);
    if (!resolvedJurisdiction) return;
    update({
      resolvedJurisdiction,
      geocodes: [{ valueName: 'SAME', value: jurisdiction.sameCode }],
      mapCenter: calculatePolygonCentroid(resolvedJurisdiction.boundaryRings[0] ?? []),
    });
  };

  return (
    <div className="alert-creator-form global-action-ipaws-editor w-100">
      {/* The FEMA destination opens the editor, across both columns, in the Alert Creator's red
          destination style. */}
      <div
        className={`alert-destination-summary alert-destination-summary-ipaws alert-destination-summary-${femaDestination.tone} global-action-ipaws-destination`}
        role="status"
      >
        <strong className="d-block">{femaDestination.environment}</strong>
        {femaDestination.detail ? (
          <small className="d-block">{femaDestination.detail}</small>
        ) : null}
        <FemaEnvironmentSelect
          femaMetaQuery={femaSelectMetaQuery}
          value={femaEnvironment}
          onChange={selectFemaEnvironment}
        />
      </div>
      <div className="global-action-ipaws-map-column d-flex flex-column gap-2">
        <IpawsDeviceMap
          draft={draft}
          authorizedJurisdictions={jurisdictions}
          onAreaShapesChange={(areaShapes) => update({ areaShapes })}
        />
        <IpawsAffectedAreaSection
          draft={draft}
          findings={findings}
          permittedArea={permittedArea}
          hasReviewedDraft={hasReviewedDraft}
          validationIssues={actionIssues}
          onTextChange={({ target: { name, value } }) => update({ [name]: value })}
          onReviewDraft={() => setHasReviewedDraft(true)}
          onClearJurisdiction={() => update({ resolvedJurisdiction: null, geocodes: [] })}
          authorizedAreas={authorizedAreas}
          onSelectAuthorizedJurisdiction={selectAuthorizedJurisdiction}
        />
      </div>
      <div className="global-action-ipaws-fields">
        {draft.sent?.length > 0 && (
          <Alert variant="secondary" className="mb-0">
            <strong>{draft.sent.length} IPAWS message(s) sent by this Global Action.</strong>
            {draft.sent.at(-1)?.capIdentifier ? (
              <div>Latest: {draft.sent.at(-1).capIdentifier}</div>
            ) : null}
            {draft.sent.at(-1)?.outcome ? <div>Outcome: {draft.sent.at(-1).outcome}</div> : null}
          </Alert>
        )}
        {savedAsDraft ? (
          <Alert variant="warning" role="status" className="mb-0">
            {IPAWS_SAVED_AS_DRAFT_FINDING}
          </Alert>
        ) : null}
        <IpawsAuthoringSections
          draft={draft}
          findings={findings}
          contractOptions={authoringContractOptions}
          environment={chosenFemaEnvironment}
          authorizedWeaHandlingOptions={jurisdictionsData?.weaHandlingOptions}
          isWeaHandlingLoading={jurisdictionsQuery.isLoading || femaMetaQuery.isLoading}
          isWeaHandlingError={isJurisdictionsError || femaMetaQuery.isError}
          channelRules={channelRules}
          onDraftChange={update}
          onTextChange={({ target: { name, value } }) => update({ [name]: value })}
          onDateTimeChange={({ target: { name, value } }) =>
            update({ [name]: value ? formatIpawsDateTimeWithOffset(value) : '' })
          }
          authorizedAreas={authorizedAreas}
          permittedArea={permittedArea}
          hasReviewedDraft={hasReviewedDraft}
          onReviewDraft={() => setHasReviewedDraft(true)}
          onClearJurisdiction={() => update({ resolvedJurisdiction: null, geocodes: [] })}
          onSelectAuthorizedJurisdiction={selectAuthorizedJurisdiction}
          renderArea={false}
          showIntervalDuration
          statusHelp={
            <ul className="list-unstyled small text-muted mb-0" aria-label="Status meanings">
              {statusHelpValues.map((value) => (
                <li key={value}>
                  <strong>{value}:</strong> {IPAWS_ACTION_STATUS_HELP[value]}
                </li>
              ))}
            </ul>
          }
        />
      </div>
    </div>
  );
};

export default PublishIpawsAlert;
